import { forwardRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useLanguage } from "../i18n/LanguageProvider";
import { withLang } from "../i18n/paths";
import { LANGUAGES, LANG_CODES, LANGUAGE_SWITCHER_ENABLED } from "../i18n/config";

/**
 * Drop-in replacement for react-router's Link that keeps the active language.
 * Every internal navigation in the portfolio goes through this, so a Turkish
 * page cannot leak the visitor back into English.
 */
export const LocalizedLink = forwardRef(({ to, ...props }, ref) => {
  const { lang } = useLanguage();
  return <Link ref={ref} to={withLang(to, lang)} {...props} />;
});

LocalizedLink.displayName = "LocalizedLink";

/** navigate() that preserves the active language. */
export const useLocalizedNavigate = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();

  return useCallback(
    (to, options) => {
      if (typeof to === "number") return navigate(to);
      return navigate(withLang(to, lang), options);
    },
    [navigate, lang]
  );
};

/** Header language dropdown. Renders nothing while the feature flag is off. */
export const LanguageSwitcher = ({ className = "", compact = false }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!LANGUAGE_SWITCHER_ENABLED) return null;

  const current = LANGUAGES[lang];

  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`} data-testid="language-switcher-mobile">
        <Globe size={16} strokeWidth={1.5} className="text-zinc-500" aria-hidden="true" />
        {LANG_CODES.map((code) => (
          <button
            key={code}
            type="button"
            onClick={() => setLang(code)}
            data-testid={`language-option-${code}`}
            aria-current={code === lang ? "true" : undefined}
            className={`rounded-full px-3 py-1.5 text-sm transition-colors duration-200 ${
              code === lang ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white"
            }`}
          >
            {LANGUAGES[code].label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="language-switcher"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Language: ${current.label}`}
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm text-zinc-400 transition-colors duration-200 hover:bg-white/5 hover:text-white"
      >
        <Globe size={15} strokeWidth={1.5} aria-hidden="true" />
        {current.short}
        <ChevronDown
          size={13}
          strokeWidth={1.5}
          aria-hidden="true"
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-full z-50 mt-2 w-40 overflow-hidden rounded-xl border border-white/10 bg-[#0F0F10]/95 py-1 shadow-2xl backdrop-blur-xl"
        >
          {LANG_CODES.map((code) => (
            <li key={code} role="option" aria-selected={code === lang}>
              <button
                type="button"
                onClick={() => {
                  setLang(code);
                  setOpen(false);
                }}
                data-testid={`language-option-${code}`}
                className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors duration-200 hover:bg-white/5 ${
                  code === lang ? "text-white" : "text-zinc-400"
                }`}
              >
                {LANGUAGES[code].label}
                {code === lang && <Check size={14} strokeWidth={2} aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
